import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    IconButton,
    Modal,
    Typography,
    Fade,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Close,
    Fullscreen,
    FullscreenExit,
    PlayArrow,
    ThreeSixty
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { CategoryItem } from '../../types';
import { getUploadUrl } from '../../config/api';
import SafeImage from '../SafeImage';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import './ImageCarousel.css';
import { useCursorContext } from '../../providers/CursorProvider';

// Helper function for external video platforms
type VideoEmbedOptions = {
    loop?: boolean;
    enableJsApi?: boolean;
    origin?: string;
    controls?: boolean;
    autoplay?: boolean;
    muted?: boolean;
};

type ExternalVideoKind = 'youtube' | 'vimeo' | 'direct';

const getExternalVideoUrl = (content: any): string => {
    if (typeof content?.media_url === 'string' && content.media_url) {
        return content.media_url;
    }

    if (typeof content?.youtube_url === 'string' && content.youtube_url) {
        return content.youtube_url;
    }

    return '';
};

const getYouTubePreviewUrl = (url?: string): string => {
    if (!url) return '';

    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(youtubeRegex);
    const videoId = match ? match[1] : null;

    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : '';
};

const detectExternalVideoKind = (url: string, platform?: string): ExternalVideoKind => {
    const normalizedPlatform = (platform || '').toLowerCase();
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const vimeoRegex = /vimeo\.com\/(?:video\/)?(\d+)/;

    if (normalizedPlatform === 'youtube' || youtubeRegex.test(url)) return 'youtube';
    if (normalizedPlatform === 'vimeo' || vimeoRegex.test(url)) return 'vimeo';
    return 'direct';
};

let youtubeIframeApiPromise: Promise<any> | null = null;
const loadYouTubeIframeApi = (): Promise<any> => {
    if (youtubeIframeApiPromise) return youtubeIframeApiPromise;

    youtubeIframeApiPromise = new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
            reject(new Error('YouTube Iframe API can only load in the browser'));
            return;
        }

        const existing = (window as any).YT;
        if (existing?.Player) {
            resolve(existing);
            return;
        }

        const existingScript = document.getElementById('youtube-iframe-api');
        if (!existingScript) {
            const script = document.createElement('script');
            script.id = 'youtube-iframe-api';
            script.src = 'https://www.youtube.com/iframe_api';
            script.async = true;
            script.onerror = () => reject(new Error('Failed to load YouTube Iframe API'));
            document.body.appendChild(script);
        }

        (window as any).onYouTubeIframeAPIReady = () => {
            const YT = (window as any).YT;
            if (YT?.Player) resolve(YT);
            else reject(new Error('YouTube Iframe API loaded but YT.Player is missing'));
        };

        // Fallback: poll briefly in case API was already ready
        const start = Date.now();
        const poll = setInterval(() => {
            const YT = (window as any).YT;
            if (YT?.Player) {
                clearInterval(poll);
                resolve(YT);
            } else if (Date.now() - start > 8000) {
                clearInterval(poll);
                reject(new Error('Timed out waiting for YouTube Iframe API'));
            }
        }, 100);
    });

    return youtubeIframeApiPromise;
};

const getVideoEmbedUrl = (url: string, platform: string, options: VideoEmbedOptions = {}): string => {
    const { loop = true, enableJsApi = false, origin, controls = false, autoplay = false, muted = false } = options;

    switch (platform) {
        case 'youtube':
            const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
            const youtubeMatch = url.match(youtubeRegex);
            const videoId = youtubeMatch ? youtubeMatch[1] : null;
            if (!videoId) return '';

            // Note: looping prevents ENDED events; keep loop=false for story auto-advance.
            // enablejsapi + origin are required to read duration/progress.
            return `https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&mute=${muted ? 1 : 0}&playsinline=1&rel=0&modestbranding=1&iv_load_policy=3&disablekb=1&fs=0` +
                `&controls=${controls ? 1 : 0}` +
                (loop ? `&loop=1&playlist=${videoId}` : `&loop=0`) +
                (enableJsApi ? `&enablejsapi=1${origin ? `&origin=${encodeURIComponent(origin)}` : ''}` : '');

        case 'vimeo':
            const vimeoRegex = /vimeo\.com\/(?:video\/)?(\d+)/;
            const vimeoMatch = url.match(vimeoRegex);
            const vimeoId = vimeoMatch ? vimeoMatch[1] : null;
            if (!vimeoId) return '';

            // Vimeo Player API works via postMessage; looping prevents ENDED events.
            return `https://player.vimeo.com/video/${vimeoId}?autoplay=${autoplay ? 1 : 0}&muted=${muted ? 1 : 0}&playsinline=1&dnt=1&transparent=0&title=0&byline=0&portrait=0` +
                `&controls=${controls ? 1 : 0}` +
                (loop ? `&loop=1` : `&loop=0`);

        default:
            // For 'other' or unknown platforms, return the URL as is
            return url;
    }
};

declare global {
    interface Window {
        YT?: any;
        onYouTubeIframeAPIReady?: () => void;
    }
}

interface ImageCarouselProps {
    items: CategoryItem[];
    initialIndex: number;
    open: boolean;
    onClose: () => void;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({
    items,
    initialIndex,
    open,
    onClose
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [direction, setDirection] = useState(1);
    const { setCursorType } = useCursorContext();

    // Touch/swipe state
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [swipeStart, setSwipeStart] = useState<{ x: number; y: number; time: number } | null>(null);
    const [isZoomed, setIsZoomed] = useState(false);

    // Story functionality state (simplified approach like reference)
    const [currentStory, setCurrentStory] = useState(0);
    const [storyChildren, setStoryChildren] = useState<any[]>([]);
    const [storyAutoPlay, setStoryAutoPlay] = useState(false);
    const [storyProgress, setStoryProgress] = useState(0);
    const [videoDuration, setVideoDuration] = useState<number | null>(null);
    const [externalVideoDuration, setExternalVideoDuration] = useState<number | null>(null);
    const videoRef = React.useRef<HTMLVideoElement>(null);
    const externalIframeRef = React.useRef<HTMLIFrameElement>(null);
    const externalPlayerRef = React.useRef<any>(null);

    // Use custom hook to manage body scroll lock
    useBodyScrollLock(open);

    // Get current item and children info
    const currentItem = items[currentIndex];

    // Enhanced close handler with overflow protection
    const handleClose = useCallback(() => {
        onClose();

        // Additional safety: ensure body overflow is cleared after modal closes
        setTimeout(() => {
            const body = document.body;
            if (body.style.position === 'fixed') {
                body.style.overflow = '';
                body.style.position = '';
                body.style.top = '';
                body.style.width = '';
            }
        }, 100);
    }, [onClose]);

    // Story management functions (based on reference code)
    const loadStoriesForCurrentSlide = useCallback(() => {
        if (!currentItem) {
            setStoryChildren([]);
            return;
        }

        const children = currentItem.children || [];
        setStoryChildren(Array.isArray(children) ? children : []);
        setCurrentStory(0); // Reset to first story
    }, [currentItem]);

    const handleStoryNavigation = useCallback((dir: 'next' | 'prev') => {
        if (storyChildren.length === 0) return;

        if (dir === 'next') {
            setDirection(1);
            if (currentStory < storyChildren.length - 1) {
                setCurrentStory(prev => prev + 1);
                setStoryProgress(0); // Reset progress
            } else {
                // Move to next main slide when stories are finished (no wrap)
                setCurrentIndex((prevIndex) =>
                    prevIndex < items.length - 1 ? prevIndex + 1 : prevIndex
                );
            }
        } else if (dir === 'prev') {
            setDirection(-1);
            if (currentStory > 0) {
                setCurrentStory(prev => prev - 1);
                setStoryProgress(0); // Reset progress
            } else {
                // Move to previous main slide (no wrap)
                setCurrentIndex((prevIndex) =>
                    prevIndex > 0 ? prevIndex - 1 : prevIndex
                );
            }
        }
    }, [storyChildren.length, currentStory, items.length]);

    const handleStoryClick = useCallback((e: React.MouseEvent) => {
        if (storyChildren.length === 0) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = rect.width;

        // Left half = previous story, right half = next story
        if (clickX < width / 2) {
            handleStoryNavigation('prev');
        } else {
            handleStoryNavigation('next');
        }
    }, [storyChildren.length, handleStoryNavigation]);

    useEffect(() => {
        if (open) {
            setCurrentIndex(initialIndex);
        }
    }, [open, initialIndex]);

    // Load stories when slide changes (like reference code)
    useEffect(() => {
        if (open && currentItem) {
            loadStoriesForCurrentSlide();
        }
    }, [currentIndex, open, loadStoriesForCurrentSlide]);

    // Story autoplay functionality (images/360 and non-external flows)
    useEffect(() => {
        if (!storyAutoPlay || storyChildren.length === 0 || isZoomed) return;

        const currentStoryItem = storyChildren[currentStory];
        if (!currentStoryItem) return;

        const isVideo = !!currentStoryItem.is_video;
        const is360 = !!currentStoryItem.is_360;

        // Videos: progress is driven by timeupdate/player APIs + auto-advance on ended.
        if (isVideo) return;

        // Durations:
        // - video: actual video duration
        // - 360: fixed 5 minutes
        // - image: 5 seconds
        let storyDurationMs = 5000;
        if (is360) storyDurationMs = 5 * 60 * 1000;

        const intervalTime = 100;
        const incrementPercentage = (intervalTime / storyDurationMs) * 100;

        const interval = setInterval(() => {
            setStoryProgress(prev => {
                if (prev >= 100) {
                    handleStoryNavigation('next');
                    return 0;
                }
                return prev + incrementPercentage;
            });
        }, intervalTime);

        return () => clearInterval(interval);
    }, [storyAutoPlay, currentStory, storyChildren, isZoomed, handleStoryNavigation, videoDuration]);

    // External video progress/duration (YouTube/Vimeo only)
    useEffect(() => {
        if (!open) return;
        if (!storyAutoPlay || storyChildren.length === 0) return;

        const currentStoryItem = storyChildren[currentStory];
        const storyUrl = getExternalVideoUrl(currentStoryItem);
        const isExternalVideo = !!(storyUrl && currentStoryItem?.is_video);
        if (!isExternalVideo) return;

        const kind = detectExternalVideoKind(storyUrl, currentStoryItem?.video_platform);
        if (kind === 'direct') return; // direct video uses <video> timeupdate/onEnded

        setExternalVideoDuration(null);
        setStoryProgress(0);

        let cancelled = false;
        let youtubePollInterval: number | null = null;
        const cleanup = async () => {
            if (youtubePollInterval) {
                window.clearInterval(youtubePollInterval);
                youtubePollInterval = null;
            }
            try {
                if (externalPlayerRef.current?.destroy) {
                    externalPlayerRef.current.destroy();
                } else if (externalPlayerRef.current?.destroy?.call) {
                    externalPlayerRef.current.destroy();
                } else if (externalPlayerRef.current?.destroy) {
                    await externalPlayerRef.current.destroy();
                }
            } catch {
                // ignore
            }
            externalPlayerRef.current = null;
        };

        const platform = kind as string;

        (async () => {
            await cleanup();
            const iframeEl = externalIframeRef.current;
            if (!iframeEl) return;

            if (platform === 'vimeo') {
                try {
                    const mod = await import('@vimeo/player');
                    if (cancelled) return;
                    const Player = (mod as any).default;
                    const player = new Player(iframeEl);
                    externalPlayerRef.current = player;

                    try {
                        const duration = await player.getDuration();
                        if (!cancelled) setExternalVideoDuration(duration);
                    } catch {
                        // ignore
                    }

                    player.on('timeupdate', (data: any) => {
                        if (cancelled) return;
                        const seconds = typeof data?.seconds === 'number' ? data.seconds : 0;
                        const duration = typeof data?.duration === 'number' ? data.duration : externalVideoDuration;
                        if (duration && duration > 0) {
                            setStoryProgress(Math.min(100, (seconds / duration) * 100));
                        }
                    });

                    player.on('ended', () => {
                        if (cancelled) return;
                        handleStoryNavigation('next');
                    });
                } catch {
                    // If Vimeo API fails, fall back to a safe default (no auto-advance)
                }
                return;
            }

            // Default: YouTube
            try {
                const YT = await loadYouTubeIframeApi();
                if (cancelled) return;

                const player = new YT.Player(iframeEl, {
                    events: {
                        onReady: () => {
                            if (cancelled) return;
                            const duration = player.getDuration?.() ?? null;
                            if (typeof duration === 'number' && duration > 0) {
                                setExternalVideoDuration(duration);
                            }

                            youtubePollInterval = window.setInterval(() => {
                                if (cancelled) return;
                                const d = player.getDuration?.() ?? duration;
                                const t = player.getCurrentTime?.() ?? 0;
                                if (typeof d === 'number' && d > 0) {
                                    setStoryProgress(Math.min(100, (t / d) * 100));
                                    if (t >= d - 0.25) {
                                        handleStoryNavigation('next');
                                    }
                                }
                            }, 200);
                        },
                        onStateChange: (event: any) => {
                            if (cancelled) return;
                            if (event?.data === YT.PlayerState?.ENDED) {
                                handleStoryNavigation('next');
                            }
                        }
                    }
                });

                externalPlayerRef.current = player;
            } catch {
                // ignore
            }
        })();

        return () => {
            cancelled = true;
            void cleanup();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, storyAutoPlay, currentStory, storyChildren, handleStoryNavigation]);

    // Start autoplay when stories are loaded (from reference code)
    useEffect(() => {
        if (storyChildren.length > 0) {
            setStoryAutoPlay(true);
            setStoryProgress(0);
            // Reset video duration when switching stories
            setVideoDuration(null);
        } else {
            setStoryAutoPlay(false);
            setStoryProgress(0);
            setVideoDuration(null);
        }
    }, [storyChildren.length]);

    // Reset video duration when story changes
    useEffect(() => {
        setVideoDuration(null);
        setExternalVideoDuration(null);
        setStoryProgress(0);
    }, [currentStory]);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            switch (event.key) {
                case 'ArrowLeft':
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    handleNext();
                    break;
                case 'Escape':
                    handleClose();
                    break;
                case 'f':
                case 'F':
                    toggleFullscreen();
                    break;
            }
        };

        if (open) {
            document.addEventListener('keydown', handleKeyPress);
            return () => {
                document.removeEventListener('keydown', handleKeyPress);
            };
        }

        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
    }, [open, handleClose]);

    const handleNext = useCallback(() => {
        if (storyChildren.length > 0) {
            handleStoryNavigation('next');
        } else {
            setDirection(1);
            setCurrentIndex((prevIndex) =>
                prevIndex < items.length - 1 ? prevIndex + 1 : prevIndex
            );
        }
    }, [items.length, storyChildren.length, handleStoryNavigation]);

    const handlePrevious = useCallback(() => {
        if (storyChildren.length > 0) {
            handleStoryNavigation('prev');
        } else {
            setDirection(-1);
            setCurrentIndex((prevIndex) =>
                prevIndex > 0 ? prevIndex - 1 : prevIndex
            );
        }
    }, [items.length, storyChildren.length, handleStoryNavigation]);

    const toggleFullscreen = useCallback(() => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    }, []);

    // Touch/swipe handlers
    const minSwipeDistance = 50;
    const maxSwipeTime = 500;

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        // Don't interfere with zoom gestures (multi-touch)
        if (e.touches.length > 1 || isZoomed) return;

        const touchDown = e.touches[0].clientX;
        setTouchStart(touchDown);
        setSwipeStart({
            x: touchDown,
            y: e.touches[0].clientY,
            time: Date.now()
        });
    }, [isZoomed]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        // Don't interfere with zoom gestures (multi-touch)
        if (e.touches.length > 1 || isZoomed) return;

        setTouchEnd(e.touches[0].clientX);
    }, [isZoomed]);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        if (!touchStart || !touchEnd || isZoomed || !swipeStart) return;

        const swipeTime = Date.now() - swipeStart.time;
        const horizontalDistance = touchStart - touchEnd;
        const verticalDistance = Math.abs(swipeStart.y - (e.changedTouches[0]?.clientY || swipeStart.y));

        // Check if it's a valid swipe (not too slow, not too vertical)
        const isValidSwipe = swipeTime <= maxSwipeTime && verticalDistance < Math.abs(horizontalDistance) * 2;

        if (!isValidSwipe) return;

        const isLeftSwipe = horizontalDistance > minSwipeDistance;
        const isRightSwipe = horizontalDistance < -minSwipeDistance;

        if (isLeftSwipe && items.length > 1) {
            e.preventDefault();
            handleNext();
        } else if (isRightSwipe && items.length > 1) {
            e.preventDefault();
            handlePrevious();
        }

        // Reset swipe state
        setSwipeStart(null);
        setTouchStart(null);
        setTouchEnd(null);
    }, [touchStart, touchEnd, isZoomed, swipeStart, items.length, handleNext, handlePrevious]);

    // Handle zoom state tracking
    const handleTransform = useCallback((ref: ReactZoomPanPinchRef, state: { scale: number; positionX: number; positionY: number }) => {
        setIsZoomed(state.scale > 1.1); // Add small threshold to prevent micro-zoom issues
    }, []);

    if (!currentItem) return null;

    // Get current content: use children if available, otherwise use main item
    const currentContent = storyChildren.length > 0
        ? storyChildren[currentStory]
        : currentItem;

    // Determine media URL (Strapi-only media approach)
    const getContentMediaUrl = (content: any) => {
        const externalVideoUrl = getExternalVideoUrl(content);
        if (externalVideoUrl) {
            return externalVideoUrl;
        }

        // First check if content has media.url (Strapi media)
        if (content.media?.url) {
            return getUploadUrl(content.media.url);
        }

        // Then check if content has direct url
        if (content.url) {
            return content.url;
        }

        // For children without media objects, construct Strapi upload URL from filename
        if (content.filename) {
            // Since children don't have media objects in Strapi response,
            // we need to construct the URL based on Strapi's upload structure
            let filename = content.filename;

            // Add file extension if missing
            if (!filename.includes('.')) {
                filename += '.webp'; // Default to webp for images
            }

            // Construct Strapi upload URL - typically /uploads/[filename]
            // Use uploads/children/ subfolder structure
            return getUploadUrl(`uploads/children/${filename}`);
        }

        // Ultimate fallback to parent item URL
        return currentItem.media?.url ? getUploadUrl(currentItem.media.url) : (currentItem.url || '');
    };


    const mediaUrl = getContentMediaUrl(currentContent);

    const activeStoryItem = storyChildren.length > 0 ? storyChildren[currentStory] : null;
    const isActiveStory360 = !!activeStoryItem?.is_360;
    const hasStories = storyChildren.length > 0;
    const canGoPrev = hasStories
        ? (currentStory > 0 || (currentStory === 0 && currentIndex > 0))
        : currentIndex > 0;
    const canGoNext = hasStories
        ? (currentStory < storyChildren.length - 1 || (currentStory === storyChildren.length - 1 && currentIndex < items.length - 1))
        : currentIndex < items.length - 1;

    // Story content renderer (based on reference code)
    const renderStoryContent = () => {
        const currentStoryItem = storyChildren[currentStory];
        if (!currentStoryItem) return null;

        const storyUrl = getContentMediaUrl(currentStoryItem);
        const isExternalVideo = !!(getExternalVideoUrl(currentStoryItem) && currentStoryItem.is_video);
        const videoPlatform = currentStoryItem.video_platform || 'youtube';
        const externalKind = isExternalVideo ? detectExternalVideoKind(storyUrl, videoPlatform) : null;

        if (currentStoryItem.is_video) {
            // Check if it's an external video with media_url
            if (isExternalVideo) {
                // YouTube/Vimeo: iframe + player APIs for progress/ended
                if (externalKind === 'youtube' || externalKind === 'vimeo') {
                    const embedUrl = getVideoEmbedUrl(storyUrl, externalKind, {
                        loop: false,
                        enableJsApi: true,
                        origin: typeof window !== 'undefined' ? window.location.origin : undefined,
                        controls: true,
                        autoplay: false,
                        muted: false
                    });
                    return (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <iframe
                                ref={externalIframeRef}
                                key={`external-story-${currentIndex}-${currentStory}`}
                                src={embedUrl}
                                title={`Video ${currentStory + 1}`}
                                style={{
                                    width: '100%',
                                    height: '80vh',
                                    border: 'none',
                                    objectFit: 'contain'
                                }}
                                allow="autoplay; encrypted-media; fullscreen"
                                allowFullScreen
                            />
                        </div>
                    );
                }

                // Platform "other" (direct mp4/webm/etc): use HTML5 video so duration/ended works
                return (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <video
                            key={`external-direct-video-${currentIndex}-${currentStory}`}
                            ref={videoRef}
                            src={storyUrl}
                            playsInline
                            controls
                            controlsList="nodownload noplaybackrate noremoteplayback"
                            disablePictureInPicture
                            onLoadedMetadata={(e) => {
                                const video = e.currentTarget;
                                setVideoDuration(video.duration);
                                setStoryProgress(0);
                            }}
                            onTimeUpdate={(e) => {
                                const video = e.currentTarget;
                                if (video.duration && video.duration > 0) {
                                    setStoryProgress(Math.min(100, (video.currentTime / video.duration) * 100));
                                }
                            }}
                            onEnded={() => {
                                handleStoryNavigation('next');
                            }}
                            style={{
                                width: '100%',
                                height: 'auto',
                                maxHeight: '80vh',
                                objectFit: 'contain'
                            }}
                        />
                    </div>
                );
            }

            // Regular uploaded video
            return (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <video
                        ref={videoRef}
                        src={storyUrl}
                        playsInline
                        controls
                        controlsList="nodownload noplaybackrate noremoteplayback"
                        disablePictureInPicture
                        onLoadedMetadata={(e) => {
                            const video = e.currentTarget;
                            setVideoDuration(video.duration);
                            setStoryProgress(0);
                        }}
                        onTimeUpdate={(e) => {
                            const video = e.currentTarget;
                            if (video.duration && video.duration > 0) {
                                setStoryProgress(Math.min(100, (video.currentTime / video.duration) * 100));
                            }
                        }}
                        onEnded={() => {
                            // Auto-advance to next story when video ends
                            handleStoryNavigation('next');
                        }}
                        style={{
                            width: '100%',
                            height: 'auto',
                            maxHeight: '80vh',
                            objectFit: 'contain'
                        }}
                    />
                </div>
            );
        } else if (currentStoryItem.is_360) {
            return (
                <div style={{ width: '100%', height: '80vh', position: 'relative' }}>
                    <iframe
                        src={storyUrl}
                        title={`360° ${currentItem.title}`}
                        style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'auto', touchAction: 'auto' }}
                    />
                </div>
            );
        } else {
            // Regular image
            return (
                <div
                    className="carousel-story-stage"
                    style={{
                    width: '100%',
                    height: '80vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <TransformWrapper
                        initialScale={1}
                        minScale={1.0}
                        maxScale={4}
                        centerOnInit={true}
                        onTransformed={handleTransform}
                        doubleClick={{ disabled: false }}
                        wheel={{ disabled: false }}
                        panning={{ disabled: isZoomed ? false : true }}
                        velocityAnimation={{ disabled: true }}
                    >
                        <TransformComponent>
                            <div
                                onClick={(e) => {
                                    if (isZoomed) {
                                        e.stopPropagation();
                                    }
                                }}
                                onTouchStart={(e) => {
                                    if (isZoomed) {
                                        e.stopPropagation();
                                    }
                                }}
                                onTouchMove={(e) => {
                                    if (isZoomed) {
                                        e.stopPropagation();
                                    }
                                }}
                                onTouchEnd={(e) => {
                                    if (isZoomed) {
                                        e.stopPropagation();
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <SafeImage
                                    src={storyUrl}
                                    alt={`${currentItem.title} - ${currentStory + 1}`}
                                    style={{
                                        width: 'auto',
                                        height: 'auto',
                                        maxWidth: '100%',
                                        maxHeight: '80vh',
                                        objectFit: 'contain',
                                        pointerEvents: 'none',
                                        display: 'block'
                                    }}
                                />
                            </div>
                        </TransformComponent>
                    </TransformWrapper>
                </div>
            );
        }
    };

    const renderMedia = () => {
        // For children, check their specific properties, fall back to parent properties
        const is360 = (storyChildren.length > 0 && currentContent && 'is_360' in currentContent)
            ? currentContent.is_360
            : currentItem.is_360 || currentItem.url_360?.includes('.html');

        const isVideo = (storyChildren.length > 0 && currentContent && 'is_video' in currentContent)
            ? currentContent.is_video
            : currentItem.is_video;

        // Check if it's an external video
        const isExternalVideo = !!(currentContent && isVideo && getExternalVideoUrl(currentContent));

        const videoPlatform = (storyChildren.length > 0 && currentContent && 'video_platform' in currentContent)
            ? currentContent.video_platform || 'youtube'
            : (currentItem.is_youtube ? 'youtube' : 'youtube');

        if (is360) {
            // 360° content
            return (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <iframe
                        src={currentItem.url_360 || currentItem.url}
                        style={{
                            width: '100%',
                            height: '80vh',
                            border: 'none'
                        }}
                        title={`360° ${currentItem.title}`}
                    />
                </Box>
            );
        } else if (isVideo) {
            // Video content - check if external or uploaded
            if (isExternalVideo) {
                const embedUrl = getVideoEmbedUrl(mediaUrl, detectExternalVideoKind(mediaUrl, videoPlatform), {
                    loop: true,
                    enableJsApi: false,
                    controls: true,
                    autoplay: false,
                    muted: false
                });
                return (
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <iframe
                            src={embedUrl}
                            title={currentItem.title}
                            style={{
                                width: '100%',
                                height: '80vh',
                                border: 'none'
                            }}
                            allow="autoplay; encrypted-media; fullscreen"
                            allowFullScreen
                        />
                    </Box>
                );
            }

            // Uploaded video
            return (
                <video
                    key={`video-${currentIndex}-${currentStory}`}
                    playsInline
                    controls
                    controlsList="nodownload noplaybackrate noremoteplayback"
                    disablePictureInPicture
                    style={{
                        maxWidth: '100%',
                        maxHeight: '80vh',
                        objectFit: 'contain'
                    }}
                >
                    <source src={mediaUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
            );
        } else {
            // Image content
            return (
                <div
                    style={{
                        maxWidth: '100%',
                        maxHeight: '80vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <TransformWrapper
                        initialScale={1}
                        minScale={1.0}
                        maxScale={4}
                        centerOnInit={true}
                        onTransformed={handleTransform}
                        doubleClick={{ disabled: false }}
                        wheel={{ disabled: false }}
                        panning={{ disabled: isZoomed ? false : true }}
                        velocityAnimation={{ disabled: true }}
                    >
                        <TransformComponent>
                            <div
                                onClick={(e) => {
                                    if (isZoomed) {
                                        e.stopPropagation();
                                    }
                                }}
                                onTouchStart={(e) => {
                                    if (isZoomed) {
                                        e.stopPropagation();
                                    }
                                }}
                                onTouchMove={(e) => {
                                    if (isZoomed) {
                                        e.stopPropagation();
                                    }
                                }}
                                onTouchEnd={(e) => {
                                    if (isZoomed) {
                                        e.stopPropagation();
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    height: '100%'
                                }}
                            >
                                <SafeImage
                                    src={mediaUrl}
                                    alt={storyChildren.length > 0 ?
                                        `${currentItem.title} - ${currentStory + 1}` :
                                        currentItem.title
                                    }
                                    style={{
                                        maxWidth: '100%',
                                        whiteSpace: 'nowrap',
                                        maxHeight: '80vh',
                                        objectFit: 'contain',
                                        pointerEvents: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                />
                            </div>
                        </TransformComponent>
                    </TransformWrapper>
                </div>
            );
        }
    };

    const slideVariants = {
        enter: (d: number) => ({
            x: d > 0 ? '100%' : '-100%',
            opacity: 0
        }),
        center: {
            x: 0,
            opacity: 1
        },
        exit: (d: number) => ({
            x: d > 0 ? '-100%' : '100%',
            opacity: 0
        })
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            disableScrollLock
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}
        >
            <Fade in={open}>
                <Box
                    className="carousel-container"
                    sx={{
                        position: 'relative',
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.95)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: { xs: 1, md: 2 }
                    }}
                >
                    {/* Header Controls */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            paddingTop: 'env(safe-area-inset-top)',
                            display: 'flex',
                            flexDirection: 'column',
                            zIndex: 10
                        }}
                    >
                        {/* Story Progress Bars */}
                        {storyChildren.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    padding: '12px 16px 0 16px',
                                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
                                }}
                            >
                                {storyChildren.map((_, index) => (
                                    <Box
                                        key={index}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            // setCurrentStory(index);
                                            // setStoryProgress(0);
                                        }}
                                        sx={{
                                            flex: 1,
                                            height: 3,
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            borderRadius: 1.5,
                                            cursor: 'pointer',
                                            overflow: 'hidden',
                                            position: 'relative'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                height: '100%',
                                                backgroundColor: 'white',
                                                borderRadius: 1.5,
                                                width: index < currentStory
                                                    ? '100%'
                                                    : index === currentStory
                                                        ? `${storyProgress}%`
                                                        : '0%',
                                                transition: index === currentStory
                                                    ? 'none'
                                                    : 'width 0.3s ease'
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {/* Main Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: storyChildren.length > 0 ? '8px 16px 16px 16px' : 2,
                                background: storyChildren.length > 0
                                    ? 'transparent'
                                    : 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'white',
                                    fontSize: { xs: '1rem', md: '1.25rem' }
                                }}
                            >
                                {storyChildren.length > 0
                                    ? `${currentItem.title} (${currentStory + 1}/${storyChildren.length})`
                                    : `${currentItem.title} (${currentIndex + 1} / ${items.length})`
                                }
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton
                                    onClick={toggleFullscreen}
                                    sx={{ color: 'white' }}
                                    size="large"
                                >
                                    {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                                </IconButton>
                                <IconButton
                                    onClick={handleClose}
                                    sx={{ color: 'white' }}
                                    size="large"
                                >
                                    <Close />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>

                    {/* Main Content */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                        onTouchStart={isActiveStory360 ? undefined : handleTouchStart}
                        onTouchMove={isActiveStory360 ? undefined : handleTouchMove}
                        onTouchEnd={isActiveStory360 ? undefined : handleTouchEnd}
                        onMouseLeave={() => setCursorType('default')}
                    >
                        {items.length > 1 && !isActiveStory360 && (
                            <>
                                <Box
                                    onMouseEnter={() => setCursorType(canGoPrev ? 'arrow-left' : 'default')}
                                    onMouseLeave={() => setCursorType('default')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (canGoPrev) {
                                            handlePrevious();
                                        }
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: { md: '12%' },
                                        bottom: { md: '12%' },
                                        width: { md: '14%' },
                                        zIndex: 30,
                                        display: { xs: 'none', md: 'block' },
                                        cursor: 'none'
                                    }}
                                />
                                <Box
                                    onMouseEnter={() => setCursorType(canGoNext ? 'arrow-right' : 'default')}
                                    onMouseLeave={() => setCursorType('default')}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (canGoNext) {
                                            handleNext();
                                        }
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        right: 0,
                                        top: { md: '12%' },
                                        bottom: { md: '12%' },
                                        width: { md: '14%' },
                                        zIndex: 30,
                                        display: { xs: 'none', md: 'block' },
                                        cursor: 'none'
                                    }}
                                />
                            </>
                        )}

                        {/* Left tap area for mobile story navigation */}
                        {isMobile && storyChildren.length > 0 && !isActiveStory360 && (
                            <Box
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handlePrevious();
                                }}
                                sx={{
                                    position: 'absolute',
                                    left: 0,
                                    top: '20%',
                                    bottom: '20%',
                                    width: '25%',
                                    zIndex: 20,
                                    cursor: 'pointer',
                                    display: { xs: 'block', md: 'none' }
                                }}
                            />
                        )}

                        {/* Media Content */}
                        <AnimatePresence mode="wait" custom={direction}>
                            <motion.div
                                key={storyChildren.length > 0 ? `story-${currentIndex}-${currentStory}` : `slide-${currentIndex}`}
                                custom={direction}
                                variants={slideVariants}
                                initial="enter"
                                animate="center"
                                exit="exit"
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%',
                                    overflow: 'hidden'
                                }}
                            >
                                {storyChildren.length > 0 ? renderStoryContent() : renderMedia()}
                            </motion.div>
                        </AnimatePresence>

                        {/* Navigation arrows hidden; navigation handled via custom cursor */}

                        {/* Right tap area for mobile story navigation */}
                        {isMobile && storyChildren.length > 0 && !isActiveStory360 && (
                            <Box
                                onClick={handleNext}
                                sx={{
                                    position: 'absolute',
                                    right: 0,
                                    top: '20%',
                                    bottom: '20%',
                                    width: '25%',
                                    zIndex: 20,
                                    cursor: 'pointer',
                                    display: { xs: 'block', md: 'none' }
                                }}
                            />
                        )}
                    </Box>

                    {/* Bottom Info */}
                    {(currentItem.description) && (
                        <Box
                            sx={{
                                position: 'relative',
                                zIndex: 10,
                                padding: '0 21px',
                                textAlign: 'center'
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'white',
                                    maxWidth: '800px',
                                    margin: '0 auto'
                                }}
                            >
                                {currentItem.description}
                                {storyChildren.length > 0 && (
                                    <Typography
                                        component="span"
                                        sx={{
                                            display: 'block',
                                            mt: 1,
                                            fontSize: '0.9em',
                                            opacity: 0.8
                                        }}
                                    >
                                        Story {currentStory + 1} of {storyChildren.length}
                                    </Typography>
                                )}
                            </Typography>
                        </Box>
                    )}

                    {/* Thumbnail Strip */}
                    {items.length > 1  && (
                        <div
                            className='thumbnails-container'
                            style={{
                                position: 'absolute',
                                bottom: 'calc(50px + env(safe-area-inset-bottom))',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                gap: '8px',
                                padding: 1,
                                backgroundColor: 'rgba(0,0,0,0.5)',
                                borderRadius: 2,
                                maxWidth: '80vw',
                                overflowX: 'auto',
                            }}
                        >
                            {items.map((item, index) => {
                                const thumbUrl = item.media?.url
                                    ? getUploadUrl(item.media.url)
                                    : (item.is_youtube ? getYouTubePreviewUrl(item.youtube_url) || item.youtube_url || item.url : item.url);
                                const is360 = item.is_360 || item.url_360?.includes('.html');
                                const isVideo = item.is_video;

                                return (
                                    <Box
                                        key={index}
                                        onClick={() => setCurrentIndex(index)}
                                        sx={{
                                            width: 80,
                                            height: 60,
                                            minWidth: 80,
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            border: index === currentIndex ? '2px solid white' : '2px solid transparent',
                                            opacity: index === currentIndex ? 1 : 0.7,
                                            '&:hover': { opacity: 1 },
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'rgba(0,0,0,0.3)'
                                        }}
                                    >
                                        {is360 ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    textAlign: 'center'
                                                }}
                                            >
                                                <ThreeSixty sx={{ fontSize: 24, mb: 0.5 }} />
                                                <Typography variant="caption" sx={{ fontSize: '10px' }}>
                                                    360°
                                                </Typography>
                                            </Box>
                                        ) : isVideo ? (
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {item.is_youtube ? (
                                                    <SafeImage
                                                        src={thumbUrl}
                                                        alt={`YouTube thumbnail ${index + 1}`}
                                                        text='...'
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0
                                                        }}
                                                    />
                                                ) : thumbUrl ? (
                                                    <video
                                                        preload="metadata"
                                                        muted
                                                        playsInline
                                                        crossOrigin="anonymous"
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0
                                                        }}
                                                    >
                                                        <source src={thumbUrl} type="video/mp4" />
                                                    </video>
                                                ) : (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'white',
                                                            fontWeight: 600,
                                                            letterSpacing: '0.08em',
                                                            textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        Video
                                                    </Typography>
                                                )}
                                                <PlayArrow
                                                    sx={{
                                                        color: 'white',
                                                        fontSize: 32,
                                                        position: 'absolute',
                                                        left: '50%',
                                                        top: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        zIndex: 1,
                                                        backgroundColor: 'rgba(0,0,0,0.6)',
                                                        borderRadius: '50%',
                                                        padding: '4px'
                                                    }}
                                                />
                                            </Box>
                                        ) : (
                                            <SafeImage
                                                src={thumbUrl}
                                                alt={`Thumbnail ${index + 1}`}
                                                text='...'
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        )}
                                    </Box>
                                );
                            })}
                        </div>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default ImageCarousel;